import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';

import PlusDurationFields, { inferPlusDurationFromUser } from './PlusDurationFields';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const showToast = (title, type = 'success') => {
  window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
    detail: { type, title },
  }));
};

const UserEditModal = ({ user, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('admin');
  const { authToken, user: currentUser } = useAuth();
  const { updateUserInList } = useAdminStore();
  const [saving, setSaving] = useState(false);
  const [plusDurationType, setPlusDurationType] = useState('days');
  const [plusDurationDays, setPlusDurationDays] = useState(30);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'free',
    status: 'active',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'free',
        status: user.status || 'active',
      });
      const inferred = inferPlusDurationFromUser(user);
      setPlusDurationType(inferred.plusDurationType);
      setPlusDurationDays(inferred.plusDurationDays);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };
      if (formData.role === 'plus') {
        payload.plusDurationType = plusDurationType;
        payload.plusDurationDays = plusDurationType === 'days' ? plusDurationDays : undefined;
      }

      const result = await adminApi.updateUser({
        token: authToken,
        userId: user.id,
        userData: payload,
      });

      if (result.success) {
        updateUserInList(user.id, result.user || payload);

        if (currentUser && String(currentUser.id) === String(user.id)) {
          window.dispatchEvent(new CustomEvent('notesplus-role-updated', {
            detail: { role: formData.role },
          }));
          window.dispatchEvent(new CustomEvent('notesplus-refresh-user'));
        }

        onSuccess?.();
        onClose();
      } else {
        const errorMap = {
          PLUS_DURATION_REQUIRED: t('userEditModal.toasts.plusDurationRequired'),
          INVALID_PLUS_DURATION: t('userEditModal.toasts.invalidPlusDuration'),
        };
        showToast(errorMap[result.error] || result.error || t('userEditModal.toasts.updateFailedFallback'), 'error');
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, { fallback: t('userEditModal.toasts.genericErrorFallback') }), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10">
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">
                  {t('userEditModal.title')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userEditModal.fullNameLabel')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userEditModal.emailLabel')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userEditModal.roleLabel')}
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                  >
                    <option value="free">{t('userEditModal.roles.free')}</option>
                    <option value="plus">{t('userEditModal.roles.plus')}</option>
                    <option value="admin">{t('userEditModal.roles.admin')}</option>
                  </select>
                </div>

                {formData.role === 'plus' && (
                  <PlusDurationFields
                    plusDurationType={plusDurationType}
                    setPlusDurationType={setPlusDurationType}
                    plusDurationDays={plusDurationDays}
                    setPlusDurationDays={setPlusDurationDays}
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userEditModal.statusLabel')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                  >
                    <option value="active">{t('userEditModal.statuses.active')}</option>
                    <option value="inactive">{t('userEditModal.statuses.inactive')}</option>
                  </select>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] rounded-lg transition-colors"
                  >
                    {t('userEditModal.cancel')}
                  </button>
                  <motion.button
                    type="submit"
                    disabled={saving}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>{t('userEditModal.save')}</span>
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserEditModal;

